import { FormEvent, memo, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { getMerchantSettings } from 'features/merchants/api';
import type { Merchant, MerchantSettings } from 'features/merchants/types';
import type { MerchantsMethodsPivot } from 'features/pivots/types';
import { RequestQueryMapper } from 'util/request-query-mapper';
import { getEnvironment } from 'util/support';
import { Filters } from 'consts/merchants';
import { fetchApi } from 'infrastructure/api';
import type { MerchantPaymentMethodsRepository } from '../MerchantPaymentMethods/repository';

type Props = {
    readonly merchant: Merchant;
    readonly paymentMethodsRepository: MerchantPaymentMethodsRepository;
};

type TransactionForm = {
    method: PlaygroundMethod | null;
    tag: string;
    currency: string;
    amount: string;
    countrycode: string;
    trackingmembercode: string;
    merchantredirecturl: string;
    notificationurl: string;
    first_name: string;
    last_name: string;
    email: string;
    date_of_birth: string;
    bank: string;
    phone: string;
    address: string;
    zip: string;
    city: string;
    country: string;
    nationality: string;
    specin: string;
};

export type PlaygroundMethod = {
    readonly name: string;
    readonly tag: string;
    readonly countries: readonly string[];
    readonly currencies: readonly string[];
};

const initialForm = (): TransactionForm => ({
    method: null,
    tag: '',
    currency: '',
    amount: '1.00',
    countrycode: '',
    trackingmembercode: `bo-${Date.now()}`,
    merchantredirecturl: 'https://testapi.payments-gateway.co/notifications',
    notificationurl: 'https://testapi.payments-gateway.co/notifications',
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    bank: '',
    phone: '',
    address: '',
    zip: '',
    city: '',
    country: '',
    nationality: '',
    specin: ''
});

const isOn = (value: unknown) => value === true || value === 1 || value === '1';

const splitOptions = (value?: string | readonly string[] | null) =>
    (Array.isArray(value) ? value : String(value ?? '').split(','))
        .map(option => option.trim())
        .filter(Boolean);

export const composePlaygroundMethods = (
    methods: readonly MerchantsMethodsPivot[]
): PlaygroundMethod[] => {
    const grouped = new Map<string, {
        name: string;
        countries: Set<string>;
        currencies: Set<string>;
    }>();

    methods
        .filter(method => isOn(method.enabled) && isOn(method.paymentMethodIsAssignable))
        .forEach(method => {
            const tag = String(method.paymentMethodTag ?? '').trim().toLowerCase();
            if (!tag) return;

            const group = grouped.get(tag) ?? {
                name: String(method.paymentMethodName || tag).trim(),
                countries: new Set<string>(),
                currencies: new Set<string>()
            };
            splitOptions(method.paymentMethodForCountries)
                .forEach(country => group.countries.add(country));
            splitOptions(method.paymentMethodForCurrencies)
                .forEach(currency => group.currencies.add(currency));
            grouped.set(tag, group);
        });

    return Array.from(grouped, ([tag, method]) => ({
        tag,
        name: method.name,
        countries: Array.from(method.countries).sort(),
        currencies: Array.from(method.currencies).sort()
    })).sort((first, second) => first.name.localeCompare(second.name));
};

const getResources = <T,>(payload: any): T[] => {
    const data = payload?.data?.data ?? payload?.data ?? payload;
    return Array.isArray(data) ? data : [];
};

const findRedirectUrl = (value: unknown): string | null => {
    if (!value || typeof value !== 'object') return null;
    for (const [key, nestedValue] of Object.entries(value)) {
        if (
            key.replace(/[^a-z]/gi, '').toLowerCase() === 'redirecturl' &&
            typeof nestedValue === 'string' &&
            /^https?:\/\//i.test(nestedValue)
        ) return nestedValue;
        const nestedRedirect = findRedirectUrl(nestedValue);
        if (nestedRedirect) return nestedRedirect;
    }
    return null;
};

const MerchantPlayground = ({ merchant, paymentMethodsRepository }: Props) => {
    const channel = process.env.NODE_ENV === 'production' && !getEnvironment()
        ? 'livechannel'
        : 'testchannel';
    const [settings, setSettings] = useState<MerchantSettings | null>(null);
    const [methods, setMethods] = useState<PlaygroundMethod[]>([]);
    const [form, setForm] = useState<TransactionForm>(initialForm);
    const [isLoading, setLoading] = useState(true);
    const [isSubmitting, setSubmitting] = useState(false);
    const [httpStatus, setHttpStatus] = useState<number | null>(null);
    const [response, setResponse] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const query = RequestQueryMapper.from()
            .containsIn(Filters.merchantId, String(merchant.coreId))
            .toString();

        setLoading(true);
        setError(null);
        getMerchantSettings(query)
            .then(async settingsResponse => {
                if (!settingsResponse.ok) throw new Error(`Merchant settings HTTP ${settingsResponse.status}`);
                const settingsPayload = await settingsResponse.json();
                if (settingsPayload?.success === false) throw new Error(settingsPayload.message);
                if (!isMounted) return;
                setSettings(getResources<MerchantSettings>(settingsPayload).at(0) ?? null);
            })
            .catch(requestError => {
                if (isMounted) {
                    setError(requestError instanceof Error ? requestError.message : 'Playground data could not be loaded.');
                }
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, [merchant.coreId]);

    useEffect(() => {
        setMethods(composePlaygroundMethods(paymentMethodsRepository.merchantsMethods));
    }, [paymentMethodsRepository]);

    const requestPreview = useMemo(() => ({
        api_id: settings?.apiId ? '••••••••••••' : '',
        api_guid: settings?.apiGuid ? '••••••••••••' : '',
        channel,
        tag: form.tag,
        currency: form.currency,
        amount: form.amount,
        countrycode: form.countrycode,
        trackingmembercode: form.trackingmembercode,
        merchantredirecturl: form.merchantredirecturl,
        notificationurl: form.notificationurl,
        ...(form.specin && { specin: form.specin }),
        customer: {
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            ...(form.date_of_birth && { date_of_birth: form.date_of_birth }),
            ...(form.phone && { phone: form.phone }),
            ...(form.address && { address: form.address }),
            ...(form.zip && { zip: form.zip }),
            ...(form.city && { city: form.city }),
            ...(form.country && { country: form.country }),
            ...(form.nationality && { nationality: form.nationality })
        },
        ...(form.bank && { bank_data: { bank: Number(form.bank) } })
    }), [channel, form, settings]);

    const setField = (field: keyof TransactionForm, value: string) =>
        setForm(current => ({ ...current, [field]: value }));

    const selectMethod = (method: PlaygroundMethod | null) => setForm(current => ({
        ...current,
        method,
        tag: method?.tag ?? '',
        countrycode: method?.countries.length === 1 ? method.countries[0] : '',
        currency: method?.currencies.length === 1 ? method.currencies[0] : ''
    }));

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);
        setResponse(null);
        setHttpStatus(null);
        if (!settings?.apiId || !settings.apiGuid) {
            setError('API ID and API GUID are missing from merchant settings.');
            return;
        }

        const { method: _, ...formValues } = form;
        const {
            first_name, last_name, email, date_of_birth, bank,
            phone, address, zip, city, country, nationality, ...transaction
        } = formValues;
        const body = {
            ...transaction,
            channel,
            customer: {
                first_name, last_name, email,
                ...(date_of_birth && { date_of_birth }),
                ...(phone && { phone }), ...(address && { address }), ...(zip && { zip }),
                ...(city && { city }), ...(country && { country }), ...(nationality && { nationality })
            },
            ...(bank && { bank_data: { bank: Number(bank) } })
        };

        const redirectTab = window.open('about:blank', '_blank');
        if (redirectTab) {
            redirectTab.opener = null;
            redirectTab.document.body.textContent = 'Preparing secure payment…';
        }

        try {
            setSubmitting(true);
            const result = await fetchApi('/bo-operations/v1/transaction/submit', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'auth-id': String(settings.apiId),
                    'auth-secret': settings.apiGuid
                },
                body
            });
            setHttpStatus(result.status);
            const text = await result.text();
            let parsedResponse: unknown;
            try { parsedResponse = text ? JSON.parse(text) : null; } catch { parsedResponse = text; }
            setResponse(parsedResponse);
            const redirectUrl = findRedirectUrl(parsedResponse);
            if (result.ok && redirectUrl && redirectTab) redirectTab.location.replace(redirectUrl);
            else redirectTab?.close();
            if (!result.ok) setError(`Sandbox returned HTTP ${result.status}.`);
        } catch (requestError) {
            redirectTab?.close();
            setError(requestError instanceof Error ? requestError.message : 'Transaction request failed.');
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}><CircularProgress /></Box>;

    const fields: Array<[keyof TransactionForm, string, boolean]> = [
        ['amount', 'Amount', true], ['trackingmembercode', 'Tracking member code', true],
        ['merchantredirecturl', 'Merchant redirect URL', true], ['notificationurl', 'Notification URL', true],
        ['first_name', 'First name', true], ['last_name', 'Last name', true], ['email', 'Email', true],
        ['date_of_birth', 'Date of birth', false], ['bank', 'Bank ID', false],
        ['phone', 'Phone', false], ['address', 'Address', false], ['zip', 'ZIP / postal code', false],
        ['city', 'City', false], ['country', 'Customer country', false], ['nationality', 'Nationality', false],
        ['specin', 'Specin', false]
    ];

    return (
        <Box component='section' sx={{ py: 3 }}>
            <Typography variant='h4' gutterBottom>Transaction Playground</Typography>
            <Typography color='text.secondary' paragraph>
                Create a sandbox transaction using the selected merchant&apos;s settings and active payment methods.
            </Typography>
            <Alert severity={settings?.apiId && settings.apiGuid ? 'success' : 'warning'} sx={{ mb: 3 }}>
                {settings?.apiId && settings.apiGuid
                    ? 'API ID and API GUID were loaded from merchant settings. Credentials are never displayed.'
                    : 'API credentials are not available in merchant settings.'}
            </Alert>
            {error && <Alert severity='error' sx={{ mb: 3 }}>{error}</Alert>}

            <Box component='form' onSubmit={onSubmit}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                    <Autocomplete
                        options={methods}
                        value={form.method}
                        getOptionLabel={method => `${method.name} (${method.tag})`}
                        isOptionEqualToValue={(option, value) => option.tag === value.tag}
                        onChange={(_, method) => selectMethod(method)}
                        renderInput={params => <TextField {...params} label='Payment method' required />}
                    />
                    <TextField label='Payment method tag' value={form.tag} required disabled />
                    <Autocomplete
                        options={form.method?.currencies ?? []}
                        value={form.currency || null}
                        disabled={!form.method}
                        onChange={(_, value) => setField('currency', value ?? '')}
                        renderInput={params => <TextField {...params} label='Currency' required />}
                    />
                    <Autocomplete
                        options={form.method?.countries ?? []}
                        value={form.countrycode || null}
                        disabled={!form.method}
                        onChange={(_, value) => setField('countrycode', value ?? '')}
                        renderInput={params => <TextField {...params} label='Transaction country' required />}
                    />
                    {fields.map(([field, label, required]) => (
                        <TextField
                            key={field}
                            label={label}
                            value={String(form[field] ?? '')}
                            required={required}
                            type={field === 'email' ? 'email' : field === 'date_of_birth' ? 'date' : field === 'bank' ? 'number' : 'text'}
                            InputLabelProps={field === 'date_of_birth' ? { shrink: true } : undefined}
                            inputProps={field === 'bank' ? { min: 1, step: 1 } : undefined}
                            onChange={event => setField(field, event.target.value)}
                            helperText={field === 'specin'
                                ? 'Optional. Sent as plain text.'
                                : field === 'date_of_birth'
                                    ? 'Optional. Sent as customer.date_of_birth in YYYY-MM-DD format.'
                                    : field === 'bank'
                                        ? 'Optional. Sent as the numeric bank_data.bank value.'
                                        : undefined}
                        />
                    ))}
                </Box>
                <Stack direction='row' spacing={2} alignItems='center' sx={{ mt: 3 }}>
                    <Button type='submit' variant='contained' disabled={!settings?.apiId || !settings.apiGuid || isSubmitting || !form.method}>
                        {isSubmitting ? 'Sending…' : 'Create test transaction'}
                    </Button>
                    {isSubmitting && <CircularProgress size={22} />}
                </Stack>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 4 }}>
                <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                    <Typography variant='subtitle2' sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>Request preview</Typography>
                    <Box component='pre' sx={{ m: 0, p: 2, overflow: 'auto', fontSize: 12 }}>{JSON.stringify(requestPreview, null, 2)}</Box>
                </Paper>
                <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                    <Typography variant='subtitle2' sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                        Response {httpStatus !== null ? `— HTTP ${httpStatus}` : ''}
                    </Typography>
                    <Box component='pre' sx={{ m: 0, p: 2, overflow: 'auto', fontSize: 12, minHeight: 160 }}>
                        {response === null ? 'Submit the form to see the sandbox response.' : JSON.stringify(response, null, 2)}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default memo(MerchantPlayground);
